/**
 * Image Library Component
 * Media library modal showing uploaded images grid with search/upload/delete
 *
 * Features:
 * - Grid view of uploaded images
 * - Search/filter by filename
 * - Upload new images via ImageUploader
 * - Select image to insert into email
 * - Delete images
 * - Copy image URL to clipboard
 * - Pagination
 *
 * Created: January 4, 2026
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Pagination,
  InputGroup,
  Tabs,
  Tab,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import ImageUploader from "./ImageUploader";
import { get_email_broadcast_images, delete_email_broadcast_image } from "../../data/ApiEndpoints";

const ImageLibrary = ({ show, onHide, onSelectImage, token }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [activeTab, setActiveTab] = useState("library");
  const [selectedImage, setSelectedImage] = useState(null);

  const LIMIT = 12;

  // Fetch images
  const fetchImages = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await axios.get(get_email_broadcast_images, {
        params: {
          page,
          limit: LIMIT,
          search: search || undefined,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        setImages(response.data.data || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalImages(response.data.pagination?.total || 0);
        setCurrentPage(page);
      } else {
        toast.error(response.data.message || "Failed to fetch images");
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error(error.response?.data?.message || "Failed to fetch images");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (show && activeTab === "library") {
      fetchImages(1, searchQuery);
    }
  }, [show, activeTab]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchImages(1, searchQuery);
  };

  // Handle image select
  const handleSelectImage = (image) => {
    if (onSelectImage) {
      onSelectImage(image.file_url);
      onHide();
      toast.success("Image inserted!");
    }
  };

  // Handle copy URL
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Image URL copied to clipboard!");
  };

  // Handle delete image
  const handleDeleteImage = async (imageId, imageName) => {
    if (!window.confirm(`Delete image "${imageName}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(delete_email_broadcast_image.replace(":id", imageId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        toast.success("Image deleted successfully");
        fetchImages(currentPage, searchQuery);
      } else {
        toast.error(response.data.message || "Failed to delete image");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete image");
    }
  };

  // Handle upload complete
  const handleUploadComplete = (uploadedImage) => {
    toast.success("Upload complete! Refreshing library...");
    setActiveTab("library");
    fetchImages(1, searchQuery);
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxPages = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
    const endPage = Math.min(totalPages, startPage + maxPages - 1);

    if (currentPage > 1) {
      items.push(
        <Pagination.Prev key="prev" onClick={() => fetchImages(currentPage - 1, searchQuery)} />
      );
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => fetchImages(page, searchQuery)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (currentPage < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => fetchImages(currentPage + 1, searchQuery)} />
      );
    }

    return <Pagination className="justify-content-center mt-3">{items}</Pagination>;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Media Library
          {totalImages > 0 && (
            <Badge bg="secondary" className="ms-2">
              {totalImages} {totalImages === 1 ? "image" : "images"}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "500px" }}>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          {/* Library Tab */}
          <Tab eventKey="library" title={<><i className="bi bi-images me-2" />Library</>}>
            {/* Search Bar */}
            <Form onSubmit={handleSearch} className="mb-3">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search images by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button variant="primary" type="submit">
                  <i className="bi bi-search" />
                </Button>
                {searchQuery && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchQuery("");
                      fetchImages(1, "");
                    }}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Form>

            {/* Image Grid */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading images...</p>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-images" style={{ fontSize: "64px", color: "#ccc" }} />
                <p className="text-muted mt-3">
                  {searchQuery ? "No images found matching your search" : "No images uploaded yet"}
                </p>
                <Button variant="primary" onClick={() => setActiveTab("upload")}>
                  <i className="bi bi-cloud-upload me-2" />
                  Upload Image
                </Button>
              </div>
            ) : (
              <>
                <Row className="g-3">
                  {images.map((image) => (
                    <Col key={image.uid} xs={6} md={4} lg={3}>
                      <Card
                        className={`h-100 ${
                          selectedImage?.uid === image.uid ? "border-primary" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <div
                          style={{
                            height: "150px",
                            overflow: "hidden",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => setSelectedImage(image)}
                        >
                          <img
                            src={image.file_url}
                            alt={image.file_name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <Card.Body className="p-2">
                          <OverlayTrigger
                            overlay={<Tooltip>{image.file_name}</Tooltip>}
                          >
                            <div
                              className="small text-truncate mb-1"
                              style={{ fontSize: "12px" }}
                            >
                              {image.file_name}
                            </div>
                          </OverlayTrigger>
                          <div className="small text-muted" style={{ fontSize: "11px" }}>
                            {formatFileSize(image.file_size)}
                          </div>
                        </Card.Body>
                        <Card.Footer className="p-2 d-flex gap-1">
                          <Button
                            variant="primary"
                            size="sm"
                            className="flex-grow-1"
                            onClick={() => handleSelectImage(image)}
                          >
                            <i className="bi bi-check-circle" /> Insert
                          </Button>
                          <OverlayTrigger overlay={<Tooltip>Copy URL</Tooltip>}>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopyUrl(image.file_url)}
                            >
                              <i className="bi bi-clipboard" />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteImage(image.uid, image.file_name)}
                            >
                              <i className="bi bi-trash" />
                            </Button>
                          </OverlayTrigger>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Pagination */}
                {renderPagination()}
              </>
            )}
          </Tab>

          {/* Upload Tab */}
          <Tab eventKey="upload" title={<><i className="bi bi-cloud-upload me-2" />Upload</>}>
            <ImageUploader token={token} onUploadComplete={handleUploadComplete} />
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageLibrary;
